/* eslint-disable react/require-default-props */
import React, { useState, useEffect } from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import { Info } from '@one-thd/sui-icons';
import { Badge, Typography, CardMedia } from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import PriceFormat from './PriceFormat';
import InstantRebates from './subcomponents/common/InstantRebates';
import helpers, { PRICE_TYPES, isMobile } from '../../helpers/price-helper';
import { triggerNewRelicEvent } from '../../utils/utils';
import { SignInLink } from './subcomponents/signInLink/SignInLink';
import { CustomTooltip } from './subcomponents/common/CustomTooltip';
import { StrikeThroughPriceDisclaimer } from './subcomponents/common/StrikeThroughPriceDisclaimer';

const {
  bool, string, shape, number, oneOfType
} = proptypes;

export const PriceDetailed = ({
  channel,
  disableRangePricing = false,
  displayEachUom = false,
  hideBadge = false,
  hideBulkPrice = false,
  hideLimitPerOrder = false,
  hideRebates = false,
  hideSavingsText = false,
  omitDetails = false,
  skuModel,
  zipCode = null,
  showStartingAt = false,
  useNewMapPolicy = false,
  isMapSpecialPriceViolation = false,
  isSignInToolTip = false,
  isDailyDealsEnabled = false,
  isStrikeThroughPricingDisclaimerEnabled = false
}) => {

  const { pricing } = skuModel;
  const { isCustomerIdentified = false } = useThdCustomer() || {};
  const [openPricingDisclaimer, setOpenPricingDisclaimer] = useState(false);

  useEffect(() => {
    if (pricing?.itemId) {
      triggerNewRelicEvent({
        actionName: isCustomerIdentified ? 'PriceDisplayedAfterAuth' : 'MapPriceDisplayed',
        itemId: pricing?.itemId,
        layoutType: 'DetailedView',
        subExperience: 'PriceDetailed',
        customerStatus: isCustomerIdentified ? 'authenticated' : 'unauthenticated',
        pricing,
        isCustomerIdentified // Pass the authentication status
      });
    }
  }, [isCustomerIdentified, pricing?.itemId, useNewMapPolicy]);

  const uomWrapper = classNames('sui-text-base sui-font-bold sui-ml-2');
  const outerPriceWrapper = classNames('sui-flex sui-flex-col');
  const innerPriceWrapper = classNames('sui-flex sui-flex-row sui-leading-none');
  const responsivePriceWrapper = classNames('sui-flex sui-flex-col sm:sui-flex-row sui-leading-none');
  const strikethroughWrapper = classNames('sui-font-regular sui-text-base sui-leading-tight sui-flex sui-flex-col sui-mt-1 sm:sui-mt-0 sm:sui-ml-2');

  // Strike through pricing disclaimer
  const handleStrikeThroughPricingDisclaimer = (priceValues) => {
    return (priceValues && isStrikeThroughPricingDisclaimerEnabled ? (
      <>
        <span className="sui-cursor-pointer sui-ml-2">
          <Info color="info" size="small" onClick={() => setOpenPricingDisclaimer(true)} />
        </span>
        {openPricingDisclaimer && (
          <StrikeThroughPriceDisclaimer
            openPricingDisclaimer={openPricingDisclaimer}
            setOpenPricingDisclaimer={setOpenPricingDisclaimer}
          />
        )}
      </>
    ) : null);
  };

  // BRIO Skus
  const handleBRIOSku = () => (
    <Typography component="div" variant="body-sm">
      <div className="sui-border-t-1 sui-border-b-1 sui-border-solid sui-border-button-inactive sui-pt-2 sui-pb-2 sui-mb-4">
        Get a quote during a FREE in-home consultation
      </div>
      <div className="sui-font-bold sui-mb-1">How to Buy</div>
      <div className="sui-break-words sui-mb-5">
        {`During your free, in-home consultation one of our professional contractors will assess your home, discuss
          product and extended service options that best fit your needs and budget. Our expert will review financing
          options and provide a total project cost estimate. Your quote will include product, labor, and any
          additional parts or extended service options. Product availability may be limited.`}
      </div>
      <CardMedia
        src="https://assets.thdstatic.com/images/v1/home-services-icon.svg"
        className="sui-max-w-[132px]"
        alt="Home Services"
        title="Home Services"
      />
    </Typography>
  );

  // HIDE_PRICE/LIVE_GOODS
  const handleHidePrice = () => (
    <Typography variant="body-lg" weight="bold" component="div">
      <div className="sui-break-words">Contact your local store for price</div>
    </Typography>
  );

  // Map price
  const handleMapPrice = ({
    mapPrice, pricingValues, uom, priceBadge, pricingMessage, hasEcoRebates, mapPolicy, save, hasStrikeThrough
  }) => {
    const tooltipContent = 'We\'ve set this item\'s price lower than the manufacturer will let us advertise, so you\'ll have to add it to your cart to see our price. If you decide you don\'t want to purchase the item, you can remove it from your cart.';
    const hasLowerPrice = /See Low Price in Cart|See Lower Price in Cart|see final price in cart/.test(pricingMessage);

    // eslint-disable-next-line no-param-reassign
    hasStrikeThrough = useNewMapPolicy ? hasStrikeThrough : true;

    let priceToDisplay = '';
    if (mapPrice) {
      priceToDisplay = mapPrice.wasPrice;
      // eslint-disable-next-line no-param-reassign
      hasStrikeThrough = mapPrice.hasStrikeThrough;
    } else if (pricingValues) {
      priceToDisplay = useNewMapPolicy && pricingValues.nowPrice ? pricingValues.nowPrice : pricingValues.wasPrice;
    }
    const lowPriceType = isMobile(channel) ? 'lower-price-small' : 'lower-price';
    const savingsExists = save?.dollarOff && save?.percentageOff;

    let mapPolicyMessage;
    if (useNewMapPolicy && mapPolicy === 'showSpecialPriceIfAuth' && !isCustomerIdentified) {
      mapPolicyMessage = <SignInLink layoutType="detailed" itemId={pricing?.itemId} isSignInToolTip={isSignInToolTip} />
    } else if (mapPolicy !== 'showSpecialPriceIfAuth') {
      mapPolicyMessage = <CustomTooltip pricingMessage={pricingMessage}>{tooltipContent}</CustomTooltip>;
    }

    return (
      <div className={outerPriceWrapper}>
        <div className="sui-inline-block">
          {(mapPrice || pricingValues) && (
            <div className={innerPriceWrapper}>
              {(!hasLowerPrice && !hideBadge && !priceBadge && priceToDisplay && !mapPolicy) && (
                <Badge type={lowPriceType} className="sui-mr-[20px]" />
              )}
              <PriceFormat
                price={priceToDisplay}
                priceBadge={priceBadge}
                isSmallBadge={false}
                isSmallSize={false}
                hideBadge={hideBadge}
                hasStrikeThrough={hasStrikeThrough}
              />
              {(uom && priceToDisplay) && (
                <span className={uomWrapper}>{helpers.parseUom(uom, displayEachUom)} </span>
              )}
              {(useNewMapPolicy && savingsExists) && (
                <div className="sui-flex sui-flex-row">
                  <div className={strikethroughWrapper}>
                    <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
                    <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
                  </div>
                  {handleStrikeThroughPricingDisclaimer(pricingValues)}
                </div>
              )}
            </div>
          )}
          {mapPolicyMessage}
        </div>
        {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
      </div>
    );
  };

  // Limit per order with strike through
  const handleLimitPerOrder = ({
    price, pricingValues, priceBadge, uom, save, hasEcoRebates,
    bulkPrice, bulkPriceThresholdQty,
    mapPrice, pricingMessage, mapPolicy, hasStrikeThrough
  }) => {
    const priceToDisplay = pricingValues ? pricingValues.nowPrice : price;
    if (pricing.quantityLimit) {
      return (
        <div className={outerPriceWrapper}>
          {helpers.getLimitPerOrder(hideLimitPerOrder, pricing.quantityLimit, 'sui-text-base sui-mb-2')}
          <div>
            {(mapPrice || pricingMessage) && handleMapPrice({
              mapPrice, pricingValues, uom, priceBadge, pricingMessage, mapPolicy, save, hasStrikeThrough
            })}
            {(!mapPrice && !pricingMessage) && (
              <div className={responsivePriceWrapper}>
                <div className={innerPriceWrapper}>
                  <PriceFormat
                    price={priceToDisplay}
                    priceBadge={priceBadge}
                    isSmallBadge={false}
                    isSmallSize={false}
                    hideBadge={hideBadge}
                  />
                  {uom && <span className="sui-font-bold sui-ml-2">{helpers.parseUom(uom, displayEachUom)} </span>}
                </div>
                <div className="sui-flex sui-flex-row">
                  <div className={strikethroughWrapper}>
                    <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
                    <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
                  </div>
                  {handleStrikeThroughPricingDisclaimer(pricingValues)}
                </div>
              </div>
            )}
          </div>
          <div className="sui-mb-2">
            {helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty)}
            {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
          </div>
        </div>
      );
    }
    return '';
  };

  // Unit and case price: regular,
  // with strike through, with bulk price, with bulk price and strike through
  const handleUnitAndCasePrice = ({
    price, alternatePrice, alternateUom, uom, pricingValues,
    save, bulkPrice, bulkPriceThresholdQty, priceBadge,
    unitPricingValues, hasEcoRebates
  }) => {
    const toInlinePriceText = (value) => {
      const parsedPrice = helpers.parsePriceValue(value);
      if (!parsedPrice) return '';

      if (parsedPrice.dollars === '0') {
        return `${parsedPrice.cents}¢`;
      }
      return `$${parsedPrice.dollars}.${parsedPrice.cents}`;
    };
    const coveragePriceTemplate = toInlinePriceText(price);
    const altPriceTemplate = toInlinePriceText(alternatePrice);
    const coverageUomTemplate = uom ? helpers.parseUom(uom, displayEachUom) : '';
    const alternateUomTemplate = alternateUom ? helpers.parseUom(alternateUom, displayEachUom) : '';
    const toParentheticalDetailText = (priceText, parsedUomText, rawUom) => {
      if (!rawUom) return '';

      const detailText = [priceText, parsedUomText].filter(Boolean).join(' ');
      return detailText ? `(${detailText})` : '';
    };
    const coverageDetailText = toParentheticalDetailText(coveragePriceTemplate, coverageUomTemplate, uom);
    const alternateDetailText = toParentheticalDetailText(altPriceTemplate, alternateUomTemplate, alternateUom);
    const altUom = (alternateUom && alternateUom !== 'each') ? alternateUom : uom;
    const { unitWasPrice, unitNowPrice } = pricing;
    const pricePerUnit = unitWasPrice || unitNowPrice;

    if (pricePerUnit) {
      if (uom !== 'sq. ft.') {
        return (
          <div id="unit-case-price-includes">
            {pricing.unitsPerCase > 1 && (
              <div className="sui-font-regular sui-text-base sui-leading-none sui-mb-2">
                {pricing.unitOfMeasureCoverage}
              </div>
            )}
            {!bulkPrice && (
              <div className={responsivePriceWrapper}>
                <div className={innerPriceWrapper}>
                  <PriceFormat
                    price={alternatePrice}
                    priceBadge={priceBadge}
                    hideBadge={hideBadge}
                    isSmallBadge={false}
                    isSmallSize={false}
                  />
                  <div className={outerPriceWrapper}>
                    <span className={uomWrapper}>
                      {alternateUomTemplate}
                    </span>
                    <span className="sui-ml-2">{coverageDetailText}</span>
                  </div>
                </div>
                <div className="sui-flex sui-flex-row">
                  <div className={strikethroughWrapper}>
                    <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
                    <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
                  </div>
                  {handleStrikeThroughPricingDisclaimer(pricingValues)}
                </div>
              </div>
            )}
            {(bulkPrice && !hideBulkPrice) && (
              <div className="sui-flex sui-flex-col sui-ml-1 sui-mt-2">
                <div className={responsivePriceWrapper}>
                  <div className={innerPriceWrapper}>
                    <PriceFormat
                      price={alternatePrice}
                      priceBadge={priceBadge}
                      hideBadge={hideBadge}
                      isSmallBadge={false}
                      isSmallSize={false}
                    />
                    {!pricingValues && (
                      <div>
                        <span className={uomWrapper}>
                          {alternateUomTemplate}
                        </span>
                        {pricing.unitsPerCase > 1 && (
                          <div className="sui-mt-1 sui-ml-2">
                            <span>{coverageDetailText}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {pricingValues && (
                      <div className="sui-flex sui-flex-col sui-ml-2">
                        <span className="sui-font-bold">
                          {alternateUomTemplate}
                        </span>
                        <span>{coverageDetailText}</span>
                      </div>
                    )}
                  </div>
                  <div className="sui-flex sui-flex-row">
                    <div className={strikethroughWrapper}>
                      <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
                      <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
                    </div>
                    {handleStrikeThroughPricingDisclaimer(pricingValues)}
                  </div>
                </div>
                <span>{helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty)}</span>
              </div>
            )}
            {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
          </div>
        );
      } else if (uom === 'sq. ft.') {
        return (
          <>
            <div id="unit-case-price" className={outerPriceWrapper}>
              <span className="sui-font-regular sui-text-base sui-leading-none">{pricing.unitOfMeasureCoverage}</span>
              <div className="sui-flex sui-flex-col sm:sui-flex-row sui-mt-2 sui-leading-none">
                {price && (
                  <div className={innerPriceWrapper}>
                    <PriceFormat
                      price={price}
                      priceBadge={priceBadge}
                      hideBadge={hideBadge}
                      isSmallBadge={false}
                      isSmallSize={false}
                    />
                    <div className="sui-ml-2">
                      <span className="sui-font-bold sui-text-base sui-leading-none">
                        {coverageUomTemplate}
                      </span>
                      <div>
                        <span>{alternateDetailText}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="sui-flex sui-flex-row">
                  <div className={strikethroughWrapper}>
                    <span className="sui-text-subtle">{helpers.getWasPrice(unitPricingValues)}</span>
                    <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save, false)}</span>
                  </div>
                  {handleStrikeThroughPricingDisclaimer(unitPricingValues)}
                </div>
              </div>
            </div>
            <span className="sui-mt-2">
              {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
            </span>
            {helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty, alternateUom, displayEachUom)}
          </>
        );
      }
    }
    return (
      <div id="unit-price">
        {helpers.getLimitPerOrder(hideLimitPerOrder, pricing.quantityLimit, 'sui-text-base sui-mb-2')}
        <div className={responsivePriceWrapper}>
          <div className={innerPriceWrapper}>
            <PriceFormat
              price={alternatePrice}
              priceBadge={priceBadge}
              isSmallBadge={false}
              isSmallSize={false}
              hideBadge={hideBadge}
            />
          </div>
          <div className={outerPriceWrapper}>
            <span className={uomWrapper}>{altUom && helpers.parseUom(altUom, displayEachUom)}</span>
          </div>
          <div className="sui-flex sui-flex-row">
            <div className={strikethroughWrapper}>
              <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
              <span className="sui-text-success">{helpers.getSavingsText(true, hideSavingsText, save)}</span>
            </div>
            {handleStrikeThroughPricingDisclaimer(pricingValues)}
          </div>
        </div>
        {helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty, alternateUom, displayEachUom)}
        {helpers.getRebates(hideRebates, hasEcoRebates, pricing.itemId, zipCode, channel)}
      </div>
    );
  };

  // Was price
  const handleWasPrice = ({
    pricingValues, save, priceBadge, uom
  }) => {
    return (
      <div id="was-price" className={responsivePriceWrapper}>
        <div className={innerPriceWrapper}>
          <PriceFormat
            price={pricingValues.nowPrice}
            priceBadge={priceBadge}
            isSmallBadge={false}
            isSmallSize={false}
            hideBadge={hideBadge}
          />
          {uom && (
            <span className="sui-ml-2 sui-font-bold sui-text-base sui-leading-none">
              {helpers.parseUom(uom, displayEachUom)}
            </span>
          )}
        </div>
        <div className="sui-flex sui-flex-row">
          <div className={strikethroughWrapper}>
            <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
            <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
          </div>
          {handleStrikeThroughPricingDisclaimer(pricingValues)}
        </div>
      </div>
    );
  };

  // Starting At price
  const handleStartingAt = ({
    pricingValues, save, price, priceBadge, uom
  }) => {
    const startingAtClass = classNames({
      'price--hidden': !showStartingAt
    });
    return (
      <div id="start-at" className={startingAtClass}>
        <div>Starting at</div>
        <PriceFormat
          price={price}
          priceBadge={priceBadge}
          hideBadge={hideBadge}
          isSmallBadge={false}
          isSmallSize={false}
        />
        {uom && <span>{helpers.parseUom(uom, displayEachUom)} </span>}
        <div className="sui-flex sui-flex-row">
          <div className={strikethroughWrapper}>
            <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
            <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
          </div>
          {handleStrikeThroughPricingDisclaimer(pricingValues)}
        </div>
      </div>
    );
  };

  // Bulk price
  const handleBulkPrice = ({
    alternateUom, price, pricingValues, bulkPrice, save, uom,
    bulkPriceThresholdQty, priceBadge
  }) => {
    const priceToDisplay = pricingValues ? pricingValues.nowPrice : price;
    return (
      <div id="bulk-price" className={outerPriceWrapper}>
        <div className={innerPriceWrapper}>
          <PriceFormat
            price={priceToDisplay}
            priceBadge={priceBadge}
            isSmallBadge={false}
            isSmallSize={false}
            hideBadge={hideBadge}
          />
          {uom && <span className={uomWrapper}>{helpers.parseUom(uom, displayEachUom)} </span>}
          <div className="sui-flex sui-flex-row">
            <div className={strikethroughWrapper}>
              <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
              <span className="sui-text-success">{helpers.getSavingsText(true, hideSavingsText, save)}</span>
            </div>
            {handleStrikeThroughPricingDisclaimer(pricingValues)}
          </div>
        </div>
        {helpers.getBulkPrice(hideBulkPrice, bulkPrice, bulkPriceThresholdQty, alternateUom, displayEachUom)}
      </div>
    );
  };

  // Instant Rebate
  const handleInstantRebate = ({
    price, pricingValues, priceBadge, uom
  }) => {
    const priceToDisplay = pricingValues ? pricingValues.nowPrice : price;
    return (
      <>
        <div id="instant-rebate" className={responsivePriceWrapper}>
          <PriceFormat
            price={priceToDisplay}
            priceBadge={priceBadge}
            isSmallBadge={false}
            isSmallSize={false}
            hideBadge={hideBadge}
          />
          <div className="sui-flex sui-flex-col sui-ml-2 sui-mt-2 sm:sui-mt-0">
            {uom && <span>{helpers.parseUom(uom, displayEachUom)} </span>}
            <div className="sui-flex sui-flex-row">
              <span className="sui-font-regular sui-text-base sui-leading-none sui-color-subtle">
                {helpers.getWasPrice(pricingValues)}
              </span>
              {handleStrikeThroughPricingDisclaimer(pricingValues)}
            </div>
          </div>
        </div>
        {!hideRebates && (
          <InstantRebates
            storePromotion={pricing.storePromotion}
            price={pricing}
            hasDetails
          />
        )}
      </>
    );
  };

  // Eco Rebate
  const handleEcoRebates = ({
    price, priceBadge, uom, pricingValues, save,
    mapPrice, pricingMessage, bulkPrice, bulkPriceThresholdQty, mapPolicy, hasStrikeThrough
  }) => {
    const priceToDisplay = pricingValues ? pricingValues.nowPrice : price;
    return (
      <div id="eco-rebate-price" className="sui-mb-1">
        {(bulkPrice && !hideBulkPrice) && handleBulkPrice({
          price, priceBadge, uom, pricingValues, save, bulkPrice, bulkPriceThresholdQty
        })}
        {(mapPrice || pricingMessage) && handleMapPrice({
          mapPrice, pricingValues, uom, priceBadge, pricingMessage, mapPolicy, save, hasStrikeThrough
        })}
        {(!mapPrice && !pricingMessage && !bulkPrice) && (
          <div className={responsivePriceWrapper}>
            <PriceFormat
              price={priceToDisplay}
              priceBadge={priceBadge}
              isSmallBadge={false}
              isSmallSize={false}
              hideBadge={hideBadge}
            />
            <div className="sui-flex sui-flex-row">
              <div className="sui-flex sui-flex-col sm:sui-ml-2 sui-mt-1 sui-mb-2 sm:sui-mt-0">
                {uom && (
                  <span>{uom && helpers.parseUom(uom, displayEachUom)} </span>
                )}
                <span className="sui-font-regular sui-text-base sui-leading-none sui-text-subtle">
                  {helpers.getWasPrice(pricingValues)}
                </span>
                <span className="sui-font-regular sui-text-base sui-leading-tight sui-text-success">
                  {helpers.getSavingsText(false, hideSavingsText, save)}
                </span>
              </div>
              {handleStrikeThroughPricingDisclaimer(pricingValues)}
            </div>
          </div>
        )}
        {helpers.getRebates(hideRebates, true, pricing.itemId, zipCode, channel)}
      </div>
    );
  };

  // No details
  const handleNoDetailsPrice = ({
    price, uom, pricingValues, save, priceBadge
  }) => (
    <div id="no-details-price">
      {price && (
        <>
          <PriceFormat
            price={price}
            priceBadge={priceBadge}
            isSmallBadge={false}
            isSmallSize={false}
            hideBadge={hideBadge}
          />
          <div className="sui-inline-block sui-ml-1">
            {uom && <span>{helpers.parseUom(uom, displayEachUom)} </span>}
            <div className="sui-flex sui-flex-row">
              <div className={strikethroughWrapper}>
                <span className="sui-text-subtle">{helpers.getWasPrice(pricingValues)}</span>
                <span className="sui-text-success">{helpers.getSavingsText(false, hideSavingsText, save)}</span>
              </div>
              {handleStrikeThroughPricingDisclaimer(pricingValues)}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Standard price
  const handleStandardPrice = ({
    price, priceBadge, uom
  }) => (
    <div id="standard-price" className={innerPriceWrapper}>
      <PriceFormat
        price={price}
        priceBadge={priceBadge}
        isSmallBadge={false}
        isSmallSize={false}
        hideBadge={hideBadge}
      />
      {uom && <span className={uomWrapper}>{helpers.parseUom(uom, displayEachUom)}</span>}
    </div>
  );

  // Range Price
  const handleRangePrice = ({
    minPrice, maxPrice, wasMinPrice, wasMaxPrice, save
  }) => (
    <div id="range-price">
      <div>
        <PriceFormat
          price={minPrice}
          isSmallSize={false}
          isSmallBadge={false}
          hideBadge={hideBadge}
        />
        <span className="sui-font-bold sui-text-3xl"> - </span>
        <PriceFormat
          price={maxPrice}
          isSmallSize={false}
          isSmallBadge={false}
          hideBadge={hideBadge}
        />
      </div>
      <div className="sui-flex sui-flex-row">
        <div>
          {helpers.getWasPriceRange(wasMinPrice, wasMaxPrice)}
          {helpers.getSavingsText(true, hideSavingsText, save, false)}
        </div>
        {handleStrikeThroughPricingDisclaimer(wasMinPrice)}
      </div>
    </div>
  );

  const renderByType = ({ _price, type }) => {
    switch (type) {
      case PRICE_TYPES.STANDARD:
        return handleStandardPrice(_price);
      case PRICE_TYPES.STRIKE_THROUGH:
        return handleWasPrice(_price);
      case PRICE_TYPES.STARTING_AT:
        return handleStartingAt(_price);
      case PRICE_TYPES.MAP_PRICE:
        return handleMapPrice(_price);
      case PRICE_TYPES.ECO_REBATE:
        return handleEcoRebates(_price);
      case PRICE_TYPES.INSTANT_REBATE:
        return handleInstantRebate(_price);
      case PRICE_TYPES.BULK_PRICE:
        return handleBulkPrice(_price);
      case PRICE_TYPES.UNIT_AND_CASE:
        return handleUnitAndCasePrice(_price);
      case PRICE_TYPES.LIMIT_PER_ORDER:
        return handleLimitPerOrder(_price);
      case PRICE_TYPES.BRIO:
        return handleBRIOSku();
      case PRICE_TYPES.LIVE_GOODS:
        return handleHidePrice();
      case PRICE_TYPES.NO_DETAILS:
        return handleNoDetailsPrice(_price);
      case PRICE_TYPES.RANGE_PRICE:
        return handleRangePrice(_price);
      default: return null;
    }
  };

  return (
    <div className="sui-flex sui-flex-col">
      {renderByType(helpers.getPricingObject({
        skuModel, displayEachUom, omitDetails, basic: false, disableRangePricing, useNewMapPolicy, isDailyDealsEnabled, isMapSpecialPriceViolation
      }))}
    </div>
  );
};

PriceDetailed.propTypes = {
  channel: string.isRequired,
  disableRangePricing: bool,
  displayEachUom: bool,
  hideBadge: bool,
  hideBulkPrice: bool,
  hideLimitPerOrder: bool,
  hideRebates: bool,
  hideSavingsText: bool,
  omitDetails: bool,
  skuModel: shape({
    pricing: shape({
      uom: string,
      unitsPerCase: number,
      wasPrice: oneOfType([number, string]),
      nowPrice: oneOfType([number, string]),
      specialBuyPrice: oneOfType([number, string]),
      dollarOff: oneOfType([number, string]),
      percentageOff: oneOfType([number, string]),
      specialBuySavings: oneOfType([number, string]),
      specialBuyDollarOff: oneOfType([number, string]),
      alternatePriceDisplay: bool,
      quantityLimit: oneOfType([number, string]),
      itemId: oneOfType([number, string]),
      unitWasPrice: oneOfType([number, string]),
      unitNowPrice: oneOfType([number, string]),
      storePromotion: shape(),
      unitOfMeasureCoverage: string,
      mapPolicy: bool,
    }).isRequired
  }).isRequired,
  zipCode: string,
  showStartingAt: bool,
  useNewMapPolicy: bool,
  isMapSpecialPriceViolation: bool,
  isSignInToolTip: bool,
  isDailyDealsEnabled: bool,
  isStrikeThroughPricingDisclaimerEnabled: bool
};
